import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Scene } from "@babylonjs/core/scene";
import type { VisualContentEntry, VisualContentMetrics } from "../config/catalog-schema";
import type { TargetDefinition } from "../core/types";
import { createPrototypeEnvironment, createPrototypeGround } from "../scene/reveals";
import { VisualAssetCache } from "./VisualAssetCache";
import { VisualCatalogRepository } from "./VisualCatalogRepository";

export interface ContentLoadResult {
  readonly id: string;
  readonly version: string;
  readonly source: "procedural_placeholder" | "glb";
  readonly fallbackUsed: boolean;
  readonly metrics: VisualContentMetrics;
  readonly error: string | null;
}

export interface RevealResult {
  readonly catalogVersion: string;
  readonly ground: ContentLoadResult;
  readonly environment: ContentLoadResult;
  readonly meshCount: number;
  readonly researchEligible: false;
}

export class RevealContentManager {
  readonly revealRoot: TransformNode;
  private readonly catalog = new VisualCatalogRepository();
  private readonly assetCache = new VisualAssetCache(4);
  private catalogVersion = "unloaded";
  private activeRoot: TransformNode | null = null;
  private qualityTier = 0;
  private generation = 0;

  constructor(private readonly scene: Scene) {
    this.revealRoot = new TransformNode("RevealRoot", scene);
  }

  async initialize(): Promise<string> {
    this.catalogVersion = (await this.catalog.load()).catalogVersion;
    return this.catalogVersion;
  }

  setQualityTier(tier: number): void {
    this.qualityTier = Math.max(0, Math.min(3, Math.floor(tier)));
  }

  clear(): void {
    this.generation += 1;
    if (!this.activeRoot) return;
    this.activeRoot.dispose(false, true);
    this.activeRoot = null;
  }

  getActiveMeshCount(): number {
    return this.revealRoot.getChildMeshes(false).length;
  }

  async reveal(target: TargetDefinition, anchor: Vector3, yaw: number): Promise<RevealResult> {
    this.clear();
    const generation = this.generation;
    const activeRoot = new TransformNode("reveal-instance", this.scene);
    activeRoot.parent = this.revealRoot;
    activeRoot.position.copyFrom(anchor);
    activeRoot.rotation.y = yaw;
    this.activeRoot = activeRoot;

    const [ground, environment] = await Promise.all([
      this.mount(this.catalog.getRequired(target.groundId), activeRoot),
      this.mount(this.catalog.getRequired(target.environmentId), activeRoot),
    ]);
    if (generation !== this.generation || this.activeRoot !== activeRoot) {
      activeRoot.dispose(false, true);
      throw new Error("Stale reveal load was discarded");
    }
    return {
      catalogVersion: this.catalogVersion,
      ground,
      environment,
      meshCount: this.getActiveMeshCount(),
      researchEligible: false,
    };
  }

  private async mount(entry: VisualContentEntry, parent: TransformNode): Promise<ContentLoadResult> {
    const mount = new TransformNode(`content-${entry.id}`, this.scene);
    mount.parent = parent;
    applyTransform(mount, entry);
    if (entry.source === "procedural_placeholder") {
      this.mountPrototype(entry, mount);
      return resultFor(entry, false, entry.declaredMetrics, null);
    }

    try {
      const asset = await this.assetCache.get(entry, this.scene);
      const instance = asset.container.instantiateModelsToScene(
        (name) => `${entry.id}:${name}`,
        true,
        { doNotInstantiate: !entry.instanced },
      );
      instance.rootNodes.forEach((node) => {
        if (!node.parent) node.parent = mount;
      });
      return resultFor(entry, false, asset.metrics, null);
    } catch (error) {
      this.mountPrototype(entry, mount);
      return resultFor(entry, true, entry.declaredMetrics, errorMessage(error));
    }
  }

  private mountPrototype(entry: VisualContentEntry, mount: TransformNode): void {
    if (entry.category === "ground") {
      createPrototypeGround(this.scene, mount, entry.id, this.qualityTier);
    } else {
      createPrototypeEnvironment(this.scene, mount, entry.id, this.qualityTier);
    }
  }
}

function applyTransform(node: TransformNode, entry: VisualContentEntry): void {
  const { scale, rotationDegrees, anchor } = entry.transform;
  node.scaling.set(scale[0], scale[1], scale[2]);
  node.position.set(anchor[0], anchor[1], anchor[2]);
  node.rotation.set(
    (rotationDegrees[0] * Math.PI) / 180,
    (rotationDegrees[1] * Math.PI) / 180,
    (rotationDegrees[2] * Math.PI) / 180,
  );
}

function resultFor(
  entry: VisualContentEntry,
  fallbackUsed: boolean,
  metrics: VisualContentMetrics,
  error: string | null,
): ContentLoadResult {
  return { id: entry.id, version: entry.version, source: entry.source, fallbackUsed, metrics, error };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
